import React, { createContext, useContext, useEffect, useState } from "react";
import { api, saveSession, clearSession, getStoredUser, getToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(!!getToken());

  useEffect(() => {
    let cancelled = false;
    async function verify() {
      if (!getToken()) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch {
        clearSession();
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    verify();
    return () => { cancelled = true; };
  }, []);

  async function login(identifier, password) {
    const { data } = await api.post("/auth/login", { identifier, password });
    saveSession(data.access_token, data.user);
    setUser(data.user);
    return data.user;
  }

  async function refresh() {
    try {
      const { data } = await api.get("/auth/me");
      setUser((u) => ({ ...(u || {}), ...data }));
      return data;
    } catch { return null; }
  }

  function logout() {
    clearSession();
    setUser(null);
  }

  const value = { user, loading, login, logout, setUser, refresh };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export const ROLE_ACCESS = {
  property: ["administrator"],
  analytics: ["administrator", "poultry_supervisor"],
  poultry: ["administrator", "poultry_supervisor", "guest"],
  health: ["administrator", "health_caregiver", "guest"],
  projects: ["administrator", "construction_supervisor", "guest"],
  finance: ["administrator", "finance_officer", "guest"],
  inventory: ["administrator", "inventory_officer", "guest"],
  assets: ["administrator", "guest"],
  farm: ["administrator", "guest"],
  documents: ["administrator", "poultry_supervisor", "health_caregiver", "construction_supervisor", "finance_officer", "inventory_officer", "guest"],
  reports: ["administrator", "poultry_supervisor", "health_caregiver", "construction_supervisor", "finance_officer", "inventory_officer", "guest"],
  notifications: ["administrator", "poultry_supervisor", "health_caregiver", "construction_supervisor", "finance_officer", "inventory_officer", "guest"],
  settings: ["administrator"],
};

export function canAccess(user, module) {
  if (!user) return false;
  const allowed = ROLE_ACCESS[module];
  if (!allowed) return true;
  return allowed.includes(user.role);
}
